import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader, ItemStorageLocations, BackButton } from '../../components';
import { AlertSVG } from '../../components/svgs';
import { SelectedSpecimenQuantity } from '../../components/specimen-tables/SelectSpecimens';
import TransactionLabelField from '../../components/transaction/TransactionLabelField';
import SpecimenLotInfo from '../../components/specimen-lot-info/SpecimenLotInfo';
import { dateFormatMMDDYYYY } from '../../utils/commonUtils';
import { TransactionTypeEnum } from '../../types/enums';
import {
  DiscardComplete,
  EmbryoStraw,
  InventoryTransaction,
  Specimen,
  SpecimenWithOptionalRequiredFields,
} from '../../types/interfaces';
import { getInventoryTransactionById } from '../../api/inventoryTransactionsApi';
import { completeDiscard } from '../../api/inventoryApi';
import { getEmbryoStraws } from '../../api/embryoStrawsApi';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TRANSACTION_CONSTANTS,
} from '../../constants/common';
import './transactionManagement.scss';

const TransactionDiscard = () => {
  const { state: locationState } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState<InventoryTransaction>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [quantities, setQuantities] = useState<SelectedSpecimenQuantity[]>([
    { specimenId: 0, availableQuantity: 0, quantity: 0, selected: false, selectedCanisterId: 0, specimenLocationId: 0 },
  ]);
  const [completingDiscard, setCompletingDiscard] = useState<boolean>(true);

  useEffect(() => {
    const getTransactionAndEmbryoStraws = async () => {
      setIsLoading(true);
      try {
        const { data } = await getInventoryTransactionById(+id!, {
          include: 'Account, Specimen.Animal, Specimen.SpecimenLocations.StorageCanister.StorageTank.StorageSite',
        });

        const updatedTransaction = {
          ...data,
          changeQuantity: Math.abs(data.changeQuantity ?? 0),
          notes: data.notes ?? '',
        };

        setTransaction(updatedTransaction);
        setNotes(updatedTransaction.notes);
        if (updatedTransaction.accountId && updatedTransaction.inventoryTransactionId) {
          const params = {
            filter: `AccountId eq ${updatedTransaction.accountId};InventoryTransactionId eq ${updatedTransaction.inventoryTransactionId};`,
          };
          const response = await getEmbryoStraws(params);
          const embryoStraws: EmbryoStraw[] = response.data ?? [];

          const isFemale = updatedTransaction.specimen?.animal?.gender?.toLowerCase() === 'female';

          const updatedSpecimenLocations =
            updatedTransaction.specimen?.specimenLocations
              ?.map(location => {
                const canisterId = location.storageCanisterId;

                const matchingStraws = embryoStraws.filter(
                  straw => straw.storageCanisterId !== null && straw.storageCanisterId === canisterId,
                );

                if (isFemale && matchingStraws.length === 0) {
                  return null;
                }

                return {
                  ...location,
                  embryoStraws: matchingStraws,
                };
              })
              .filter((loc): loc is NonNullable<typeof loc> => loc !== null) ?? [];

          const updatedSpecimen: SpecimenWithOptionalRequiredFields = {
            ...updatedTransaction.specimen,
            specimenLocations: updatedSpecimenLocations,
          };

          setTransaction({
            ...updatedTransaction,
            specimen: updatedSpecimen as Specimen,
          });
        }
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    getTransactionAndEmbryoStraws();
  }, [id]);

  const onSubmit = async () => {
    if (!completingDiscard) return;
    setCompletingDiscard(false);

    if (!isFormDisabled(true)) {
      const discardSpecimensMap = quantities
        .filter(quantityItem => quantityItem.selected)
        .reduce((acc, quantityItem) => {
          const key = quantityItem.specimenId;

          if (!acc[key]) {
            acc[key] = {
              specimenId: quantityItem.specimenId,
              quantity: 0,
              selectedCanisterId: quantityItem.selectedCanisterId || 0,
              selectedEmbryoStrawIds: [] as number[],
            };
          }

          acc[key].quantity += quantityItem.quantity || quantityItem.availableQuantity || 0;

          if (quantityItem.embryoStrawId) {
            acc[key].selectedEmbryoStrawIds.push(quantityItem.embryoStrawId);
          }

          return acc;
        }, {} as Record<number, { specimenId: number; quantity: number; selectedCanisterId: number; selectedEmbryoStrawIds: number[] }>);

      const mappedDiscardSpecimens: DiscardComplete[] = Object.values(discardSpecimensMap).map(item => ({
        specimenId: item.specimenId,
        quantity: item.quantity,
        selectedCanisterId: item.selectedCanisterId,
        selectedEmbryoStrawIds: item.selectedEmbryoStrawIds.length ? item.selectedEmbryoStrawIds : undefined,
      }));

      setIsLoading(true);
      try {
        if (!transaction?.inventoryTransactionId) {
          throw new Error(LABEL_CONSTANTS.TRANSACTION_ID_NOT_FOUND);
        }

        await completeDiscard(transaction.inventoryTransactionId, mappedDiscardSpecimens, notes);

        showToast.success(toastMessages.DISCARD_SUCCESSFUL);
        navigate(ROUTE_PATHS.APP_TRANSACTION_MANAGEMENT, {
          state: {
            filterPreset: locationState.transactionManagementFilterPreset,
            filter: locationState.filter,
          },
        });
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
        setCompletingDiscard(true);
      }
    } else {
      setCompletingDiscard(true);
    }
  };

  const isFormDisabled = (showToasts?: boolean) => {
    if (Number(transaction?.changeQuantity) !== quantities.filter(q => q.selected).reduce((a, b) => a + Number(b.quantity), 0)) {
      showToasts && showToast.error(toastMessages.TRANSACTION_DISCARD_MISMATCH);
      return true;
    }
    if (quantities.some(item => item.quantity > item.availableQuantity)) {
      showToasts && showToast.error(toastMessages.INVALID_DISCARD_QUANTITY);
      return true;
    }
    if (quantities.some(item => item.selected && item.quantity === 0)) {
      showToasts && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return true;
    }
    return false;
  };

  return (
    <div>
      {locationState && locationState.transactionManagementFilterPreset && (
        <BackButton
          onClick={() =>
            navigate(ROUTE_PATHS.APP_TRANSACTION_MANAGEMENT, {
              state: { filterPreset: locationState.transactionManagementFilterPreset, filter: locationState.filter },
            })
          }
        />
      )}
      <div className="inventory-action card inventory-discard">
        {isLoading && <Loader loaderSize={'medium'} pageLoader />}
        <h1>{PAGE_HEADER_CONSTANTS.COMPLETE_DISCARD}</h1>
        <h4>{TRANSACTION_CONSTANTS.TRANSACTION_INFORMATION}</h4>
        <hr />

        <TransactionLabelField label={`${TRANSACTION_CONSTANTS.TRANSACTION_ID}:`} value={id} />
        <TransactionLabelField
          label={`${LABEL_CONSTANTS.ACCOUNT_OWNER}:`}
          value={transaction?.account?.accountId + '-' + transaction?.account?.name}
        />
        <TransactionLabelField label={`${ANIMAL_CONSTANTS.ANIMAL_NAME}:`} value={transaction?.specimen?.animal?.name} />
        <TransactionLabelField
          label={`${LABEL_CONSTANTS.INVENTORY_LOT_NUMBER}:`}
          value={
            <SpecimenLotInfo
              specimenId={transaction?.specimen?.specimenId!}
              lotNumber={transaction?.specimen?.lotNumber}
              freezeDate={transaction?.specimen?.freezeDate}
            />
          }
        />
        <TransactionLabelField label={`${LABEL_CONSTANTS.QUANTITY_TO_DISCARD}:`} value={transaction?.changeQuantity} />
        <TransactionLabelField
          label={`${TRANSACTION_CONSTANTS.TRANSACTION_DATE}:`}
          value={dateFormatMMDDYYYY(transaction?.createdDatetime)}
        />

        <div className="form-row">
          <label htmlFor="transaction-notes">{LABEL_CONSTANTS.NOTES}:</label>
          <div className="input-container">
            <textarea id="transaction-notes" onChange={e => setNotes(e.target.value)} value={notes} placeholder="Notes" />
          </div>
        </div>

        {!completingDiscard && <Loader loaderSize="small" simple />}
        <br />
        <h4>{LABEL_CONSTANTS.ITEM_STORAGE_LOCATIONS}</h4>
        <hr />

        {transaction?.specimen && (
          <ItemStorageLocations
            transactionType={TransactionTypeEnum.Discard}
            setQuantities={setQuantities}
            specimen={transaction?.specimen}
            quantities={quantities}
          />
        )}

        {quantities.some(q => q.selected) &&
          Number(transaction?.changeQuantity) !==
            quantities.filter(q => q.selected).reduce((a, b) => a + Number(b.quantity), 0) && (
            <div className="validated-input-message-error margin-top">
              <AlertSVG />
              Total quantity selected must equal transaction quantity {Number(transaction?.changeQuantity)}
            </div>
          )}

        <div className="flex-right margin-top">
          <button
            type="submit"
            className={isFormDisabled() ? 'button green small disabled' : 'button green small'}
            onClick={onSubmit}>
            {BUTTON_CONSTANTS.COMPLETE}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDiscard;
